import HighlightOverlay from '../CustomOverlay/HighlightOverlay/HighlightOverlay';
import HelperBadge from '../HelperBadge/HelperBadge';
import StarRating from './StarRating';
import './styles.css';
import { SurveyCardCoins } from './survey-card-coins.enum';
import { SurveyCardDataProps } from './survey-card-data-props.interface';
import { SurveyCardProps } from './survey-card-props.interface';
import { SurveyCardState } from './survey-card-state-enum';
import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import BigCoin from 'src/assets/images/icons/custom-icons/CoinBig';
import ClockPurple from 'src/assets/images/icons/default/ClockPurple';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useTranslationByKey } from 'src/hooks/use-translation-by-key';
import { useUserCurrency } from 'src/hooks/use-user-currency';

/*
 * Tailwind CSS class safelist - ensures these dynamic classes are included in the build:
 * bg-green-50 bg-amber-50 bg-red-50
 * text-green-600 text-amber-600 text-red-600
 * bg-green-400 bg-amber-400 bg-red-400
 * border-green-200 border-amber-200 border-red-200
 * from-green-400 from-amber-400 from-red-400
 * to-green-600 to-amber-600 to-red-600
 */

// Default data for the survey card
const SurveyDefaultData: SurveyCardDataProps = {
  isProfileSurvey: false,
  xpValue: 0,
  rating: 0,
  coinIcon: SurveyCardCoins.CoinBig,
  coinValue: 0,
  loiSeconds: 0,
};

// Default properties for the survey card component
const SurveyCardDefaultData: SurveyCardProps = {
  surveyData: SurveyDefaultData,
  state: SurveyCardState.Enabled,
};

/**
 * Redesigned Modern SurveyCard Component
 *
 * A cleaner, more sophisticated survey card with improved visual hierarchy,
 * glassmorphism effects, and subtle micro-interactions.
 * Features a compact design that maximizes content visibility while maintaining elegance.
 */
const SurveyCard: React.FC<SurveyCardProps> = ({
  surveyData = SurveyCardDefaultData.surveyData,
  state = SurveyCardDefaultData.state,
  className,
  highlighted = false,
  dimBackground = true,
  onClick,
  isJumping = false,
  isGlowing = false,
}) => {
  const t = useTranslationByKey('COMPONENTS.SURVEY_CARD');
  const { formatPoints } = useUserCurrency();
  const isDisabled = state === SurveyCardState.Disabled;

  /**
   * Enhanced time formatting with improved categorization
   */
  const getTimeInfo = (loiSeconds: number) => {
    const totalMinutes = Math.ceil(loiSeconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    // Format display text
    let displayText = '';
    if (hours > 0) {
      displayText =
        minutes > 0
          ? t('TIME_HOURS_MINUTES', { hours: hours.toString(), minutes: minutes.toString() })
          : t('TIME_HOURS', { hours: hours.toString() });
    } else {
      displayText = t('TIME_MINUTES', { minutes: totalMinutes.toString() });
    }

    // Determine time category with colors
    let category = '';
    let colorScheme = {
      bg: 'bg-gray-50',
      text: 'text-gray-600',
      gradient: 'from-gray-400 to-gray-600',
      border: 'border-gray-200',
    };

    if (totalMinutes <= 5) {
      category = t('QUICK');
      colorScheme = {
        bg: 'bg-green-50',
        text: 'text-green-600',
        gradient: 'from-green-400 to-green-600',
        border: 'border-green-200',
      };
    } else if (totalMinutes <= 15) {
      category = t('MEDIUM');
      colorScheme = {
        bg: 'bg-amber-50',
        text: 'text-amber-600',
        gradient: 'from-amber-400 to-amber-600',
        border: 'border-amber-200',
      };
    } else {
      category = t('LONG');
      colorScheme = {
        bg: 'bg-red-50',
        text: 'text-red-600',
        gradient: 'from-red-400 to-red-600',
        border: 'border-red-200',
      };
    }

    return {
      displayText,
      category,
      colorScheme,
      totalMinutes,
    };
  };

  // Destructuring survey data
  const { xpValue, rating, coinIcon, coinValue, loiSeconds, partnerName } = surveyData;

  // For ComponentSandbox compatibility
  const [isSurveyCardHighlighted, setIsSurveyCardHighlighted] = useState(false);

  useEffect(() => {
    setIsSurveyCardHighlighted(highlighted);
  }, [highlighted]);

  const { isMobile } = useBreakpoint();

  // Simplified interaction state
  const [isInteracting, setIsInteracting] = useState(false);

  // Get modern card styles with glassmorphism
  const getCardStyles = () => {
    const baseStyles = [
      'group',
      'survey-card-root',
      'relative',
      'w-full',
      'max-w-full',
      'h-[115px]', // Even shorter height on mobile
      'md:h-[160px]',
      'rounded-2xl',
      'overflow-hidden',
      'font-spaceGrotesk',
      'transition-all',
      'duration-300',
      'ease-out',
      'backdrop-blur-md',
      'select-none',
      'cursor-pointer',
    ];

    // Add animation classes
    if (isJumping) {
      baseStyles.push('jumping-animation');
    }
    if (isGlowing) {
      baseStyles.push('glowing-animation');
    }

    if (state === SurveyCardState.Disabled) {
      baseStyles.push(
        'survey-card-disabled',
        'bg-gray-100/40',
        'cursor-pointer',
        'opacity-60',
        'grayscale',
        'saturate-0',
        'border',
        'border-gray-200/30',
        'shadow-sm'
      );
    } else {
      // Restored colored gradient background
      baseStyles.push(
        'bg-gradient-to-br',
        'from-indigo-50/90',
        'via-blue-50/80',
        'to-purple-50/90',
        'border',
        'border-indigo-200/50',
        'shadow-xl',
        'shadow-indigo-100/30',
        'hover:shadow-2xl',
        'hover:shadow-indigo-400/30',
        'survey-card-hover-tilt'
      );

      if (isInteracting) {
        baseStyles.push(
          // Darken background on interaction
          'from-indigo-100/95',
          'via-blue-100/85',
          'to-purple-100/95'
        );
      }
    }

    if (isSurveyCardHighlighted) {
      baseStyles.push('ring-2', 'ring-indigo-400', 'ring-offset-2');

      if (dimBackground) {
        baseStyles.push('relative', 'z-[100]');
      }
    }

    return baseStyles;
  };

  // Simplified interaction handlers
  const handleInteractionStart = useCallback(() => {
    if (state !== SurveyCardState.Disabled) {
      setIsInteracting(true);
    }
  }, [state]);

  const handleInteractionEnd = useCallback(() => {
    setIsInteracting(false);
  }, []);

  // Get coin icon
  const getSurveyIcon = () => {
    switch (coinIcon) {
      case SurveyCardCoins.CoinBig:
      default:
        return <BigCoin className="w-10 h-10 md:h-9 md:w-9" />;
    }
  };

  // Time info
  const timeInfo = getTimeInfo(loiSeconds);

  const highlightOverlayZClass = dimBackground ? 'z-[110]' : 'z-10';

  // Arrow element for highlighted card
  const arrowElement = isMobile ? (
    <div className={classNames('absolute -top-[80px] right-4 animate-bounce', highlightOverlayZClass)} />
  ) : (
    <div className={classNames('absolute -top-10 -right-[80px] animate-bounce', highlightOverlayZClass)} />
  );

  // Badge element for highlighted card
  const badgeElement = (
    <div className={classNames('absolute -top-8 left-0 text-base text-white animate-pulse', highlightOverlayZClass)}>
      <HelperBadge text={t('START_HERE')} />
    </div>
  );

  // Rendering the redesigned survey card
  return (
    <>
      {isMobile && isSurveyCardHighlighted && (
        <Helmet defer={false}>
          <meta name="theme-color" content="#6366f1" />
        </Helmet>
      )}
      <HighlightOverlay
        isHighlighted={isSurveyCardHighlighted}
        dimBackground={dimBackground}
        overlay={
          <>
            {badgeElement}
            {arrowElement}
          </>
        }
      >
        <div
          onClick={onClick}
          onMouseDown={handleInteractionStart}
          onMouseUp={handleInteractionEnd}
          onMouseLeave={handleInteractionEnd}
          onTouchStart={handleInteractionStart}
          onTouchEnd={handleInteractionEnd}
          className={classNames(...getCardStyles(), className)}
          role="button"
          tabIndex={0}
          aria-label={`${formatPoints(coinValue)}, ${xpValue.toLocaleString()} XP, ${timeInfo.displayText}`}
          title={JSON.stringify(surveyData.projectDetailsDto, null, 2)} //beatify this
        >
          {/* Subtle gradient background - now more vibrant */}
          {!isDisabled && (
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-100/20 via-transparent to-purple-100/20 opacity-60 group-hover:opacity-80 transition-opacity duration-300" />
          )}

          {/* Glassmorphism noise texture */}
          {!isDisabled && (
            <div className="absolute inset-0 opacity-[0.015] mix-blend-multiply">
              <svg width="100%" height="100%">
                <filter id="noiseFilter">
                  <feTurbulence type="turbulence" baseFrequency="0.9" numOctaves="4" />
                </filter>
                <rect width="100%" height="100%" filter="url(#noiseFilter)" />
              </svg>
            </div>
          )}

          {/* Main content - reduced padding and spacing */}
          <div className="relative z-10 h-full flex flex-col p-2 sm:p-3">
            {/* Top row: Coin value and XP - reduced margin */}
            <div className="flex items-start justify-between mb-1 sm:mb-2">
              {/* Coin value with enhanced styling */}
              <div className="flex items-center gap-2">
                <div className="relative">
                  {getSurveyIcon()}
                  {/* Pulse effect on hover */}
                  {!isDisabled && (
                    <div className="absolute inset-0 rounded-full bg-yellow-400/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  )}
                </div>
                <span className="text-2xl md:text-[26px] font-bold bg-gradient-to-r from-indigo-700 to-purple-600 bg-clip-text text-transparent">
                  {formatPoints(coinValue)}
                </span>
              </div>

              {/* XP Badge - Compact and elegant */}
              <div className="px-2.5 py-1.5 bg-gradient-to-r from-emerald-500 to-green-600 rounded-full shadow-lg shadow-green-500/20 md:px-3">
                <div className="flex items-center gap-1">
                  <span className="text-white font-bold text-sm">+{xpValue}</span>
                  <span className="text-white/90 text-xs">{t('XP')}</span>
                </div>
              </div>
            </div>

            <div className="border-b-1 border-b-neutral-subtler-enabled w-full mx-auto" />
            <div className="flex justify-between items-center pt-3 sm:pt-4 mt-auto">
              <div className="min-w-0">
                {partnerName && (
                  <p className="mb-1 truncate font-manrope text-[10px] font-medium leading-3 text-slate-400">
                    {partnerName}
                  </p>
                )}
                <div className="flex items-center">
                  <StarRating rating={rating || 0} />
                  <span className="ml-1 text-content-neutral-default-enabled text-body-p1 font-medium">
                    {rating || t('NO_RATING')}
                  </span>
                </div>
              </div>

              {/* Bottom row: Time info - moved up with mt-auto */}
              <div className="flex items-center justify-end mt-auto">
                {/* Time duration */}
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/60 backdrop-blur-sm rounded-xl border border-indigo-200/30">
                  <ClockPurple className="w-4 h-4" />
                  <span className="text-sm font-semibold text-indigo-700">{timeInfo.displayText}</span>
                </div>
              </div>
            </div>
          </div>

          {!isDisabled && (
            <>
              {/* Enhanced glow effect on hover */}
              <div className="absolute -inset-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-400/20 via-purple-400/20 to-indigo-400/20 rounded-2xl blur-lg" />
              </div>

              {/* Interactive shine effect on hover - enhanced */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              </div>

              {/* Pulse accent for extra pop */}
              <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-gradient-to-br from-indigo-400/20 to-purple-400/20 rounded-full blur-2xl group-hover:scale-150 group-hover:from-indigo-500/40 group-hover:to-purple-500/40 transition-all duration-500" />

              {/* Additional rim light effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="absolute inset-0 rounded-2xl border border-white/40 shadow-inner" />
              </div>
            </>
          )}
        </div>
      </HighlightOverlay>
    </>
  );
};

export default SurveyCard;
